import { useCallback, useMemo } from 'react';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { removalConfirmationContent } from 'components/organisms/resource-management/utils/resource-removal-confirmation-content';
import { useTranslate } from 'hooks/use-translation';
import { useProjectsQuery } from 'queries';
import { useNativeTeamsInAccount } from 'queries/placeholder';
import { useRemoveTeamsFromProjectMutation } from 'queries/project';
import { logger } from 'services';

interface Props {
  projectId: number;
  teamId: number;
  onRemovalConfirm: (projectId: number, teamId: number) => void;
}

interface ReturnProps {
  showConfirmation: () => void;
}

export const useTeamRemovalConfirmation = ({
  projectId,
  teamId,
  onRemovalConfirm,
}: Props): ReturnProps => {
  const { data: projects = [] } = useProjectsQuery();
  const { data: teams = [] } = useNativeTeamsInAccount();
  const { mutateAsync: removeTeamFromProject } =
    useRemoveTeamsFromProjectMutation();

  const translate = useTranslate();

  const projectText = translate('project');
  const placeholderText = translate('placeholder').toLowerCase();
  const removeText = translate('remove');

  const team = useMemo(
    () => teams.find(({ teamId: tId }) => tId === teamId),
    [teamId, teams],
  );
  const memberName = useMemo(
    () => team?.teamName ?? placeholderText,
    [team?.teamName, placeholderText],
  );
  const projectName = useMemo(
    () =>
      projects.find((project) => project.projectId === projectId)
        ?.projectName ?? projectText,
    [projectId, projectText, projects],
  );

  const teamRemovalConfirmationTexts = useMemo(
    () => ({
      title: translate(
        'resource_management.actions.remove_member.confirmation.title',
        {
          memberType: placeholderText,
        },
      ),
      content: removalConfirmationContent(memberName, projectName, translate),
    }),
    [memberName, placeholderText, projectName, translate],
  );

  const teamRemovalTexts = useMemo(
    () => ({
      success: {
        title: translate(
          'resource_management.actions.remove_placeholder_from_project.success.title',
        ),
        message: translate(
          'resource_management.actions.remove_placeholder_from_project.success.message',
        ),
      },
      error: {
        title: translate(
          'resource_management.actions.remove_placeholder_from_project.error.title',
        ),
        message: translate(
          'resource_management.actions.remove_placeholder_from_project.error.message',
        ),
      },
    }),
    [translate],
  );

  const showConfirmation = useCallback(() => {
    if (team == null) {
      return;
    }

    confirm({
      ...teamRemovalConfirmationTexts,
      okText: removeText,
      okType: 'danger',
      onOk: async () => {
        await removeTeamFromProject({
          projectId,
          teamIds: [teamId],
        })
          .then(() => {
            onRemovalConfirm(projectId, teamId);
            toastService({
              ...teamRemovalTexts.success,
            }).success();
          })
          .catch((error) => {
            logger.error('Error when removing placeholder in RM', { error });
            toastService({
              ...teamRemovalTexts.error,
            }).error();
          });
      },
    });
  }, [
    onRemovalConfirm,
    projectId,
    removeTeamFromProject,
    removeText,
    team,
    teamId,
    teamRemovalConfirmationTexts,
    teamRemovalTexts.error,
    teamRemovalTexts.success,
  ]);

  return {
    showConfirmation,
  };
};
